import React, { useEffect, useState } from "react";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Switch,
  Tooltip,
} from "@mui/material";
import axios from "axios";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import MUIDataTable from "mui-datatables";
import AddIcon from "@mui/icons-material/Add";
import Loader from "../../components/loader/Loader";
import { Controller, useForm } from "react-hook-form";
import EditIcon from "@mui/icons-material/Edit";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { grey } from "@mui/material/colors";
import SaveIcon from "@mui/icons-material/Save";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { FormInputDropdown } from "../../components/form-components/FormInputDropdown";
import {
  hideConfirmModal,
  showConfirmModal,
  showModal,
  storeCustomPromoList,
} from "../../redux/counterSlice";
import { CurrencyRupee, Percent } from "@mui/icons-material";
import { FormInputText } from "../../components/form-components/FormInputText";
import dayjs from "dayjs";
import { FormInputDate } from "../../components/form-components/FormInputDate";
import { CustomerPickerField } from "../../components/form-components/Customerpickerfield";
import { useCustomerSearch } from "../../hooks/Usecustomersearch";

const customerPromoValidationSchema = yup.object({
  promo_code: yup.string().required("Promo code is required"),
  type: yup
    .string()
    .oneOf(["PERCENTAGE", "AMOUNT"], "Type is required")
    .required("Type is required"),
  discount: yup
    .number()
    .typeError("Discount must be a number")
    .required("Discount is required"),
  min_amount: yup
    .number()
    .transform((value, original) => (original === "" ? null : value))
    .nullable(),
  max_amount: yup
    .number()
    .transform((value, original) => (original === "" ? null : value))
    .nullable(),
  promo_applicable_count: yup
    .number()
    .transform((value, original) => (original === "" ? null : value))
    .nullable(),
  start_date: yup.mixed().nullable(),
  end_date: yup.mixed().nullable(),
  terms_conditions: yup.string().nullable(),
  customer_ids: yup.array().nullable(),
});

function CustomerPromo() {
  const dispatch = useDispatch();
  const { categoryList, customerList } = useSelector((state) => state.counter);

  const methods = useForm({
    resolver: yupResolver(customerPromoValidationSchema),
  });
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = methods;

  const [loader, setLoader] = useState(true);
  const [formData, setFormData] = useState([]);
  const [discountList, setDiscountList] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  // ✅ Shared, reusable customer search - same hook can be imported
  // into any other module that needs a fast customer picker.
  const { customerOptions, customerLoading, searchCustomers } =
    useCustomerSearch(customerList);

  const loadInitialData = async () => {
    try {
      setLoader(true);
      const response = await axios.get("/custom_promos");
      const data = response?.data?.data;
      setFormData(data);
      dispatch(storeCustomPromoList(data));
    } catch (err) {
      toast.error(err?.message);
    } finally {
      setLoader(false);
      dispatch(hideConfirmModal({ msg: "" }));
    }
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  const columns = [
    {
      name: "id",
      label: "S. No",
      options: {
        customBodyRenderLite: (dataIndex) => {
          return <p>{+dataIndex + 1}</p>;
        },
      },
    },
    {
      name: "promo_code",
      label: "Promo Code",
    },

    {
      name: "type",
      label: "Type",
      options: {
        customBodyRenderLite: (dataIndex) => {
          return formData?.[dataIndex]?.type === "PERCENTAGE" ? (
            <Percent sx={{ fontSize: 20 }} />
          ) : (
            <CurrencyRupee sx={{ fontSize: 20 }} />
          );
        },
      },
    },

    {
      name: "discount",
      label: "Amount/Percent",
      options: {
        customBodyRenderLite: (dataIndex) => {
          return (
            <p>
              {formData?.[dataIndex]?.type === "AMOUNT" && "₹ "}
              {formData[dataIndex]?.discount}
              {formData?.[dataIndex]?.type === "PERCENTAGE" && " %"}
            </p>
          );
        },
      },
    },
    {
      name: "min_amount",
      label: "Min Amount",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const val = formData[dataIndex]?.min_amount;
          return <p>{val != null ? `₹ ${val}` : "-"}</p>;
        },
      },
    },
    {
      name: "max_amount",
      label: "Max Amount",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const val = formData[dataIndex]?.max_amount;
          return <p>{val != null ? `₹ ${val}` : "-"}</p>;
        },
      },
    },
    {
      name: "new_user",
      label: "New User Only",
      options: {
        customBodyRenderLite: (dataIndex) => {
          return <p>{formData[dataIndex]?.new_user === 1 ? "Yes" : "No"}</p>;
        },
      },
    },
    {
      name: "promo_applicable_count",
      label: "Applicable Count",
      options: {
        customBodyRenderLite: (dataIndex) => {
          return <p>{formData[dataIndex]?.promo_applicable_count ?? "-"}</p>;
        },
      },
    },
    {
  name: "customer_ids",
  label: "Customers",
  options: {
    customBodyRenderLite: (dataIndex) => {
      const ids = formData[dataIndex]?.customer_ids || [];

      if (ids.length === 0) {
        return (
          <span
            style={{
              display: "inline-block",
              background: "#e0e7ff",
              color: "#3730a3",
              borderRadius: "16px",
              padding: "2px 10px",
              fontSize: "12px",
              fontWeight: 500,
            }}
          >
            All Customers
          </span>
        );
      }

      const names = ids.map((custId) => {
        const cust = customerList?.find((c) => c.customer_id === custId);
        return cust?.customer_name || custId;
      });

      const tooltipContent = (
        <div style={{ padding: "4px" }}>
          {names.map((name, idx) => (
            <div key={idx} style={{ marginBottom: "4px" }}>
              <strong>{idx + 1}.</strong> {name}
            </div>
          ))}
        </div>
      );

      return (
        <Tooltip title={tooltipContent} arrow placement="top">
          <span
            style={{
              display: "inline-block",
              background: "#e0e7ff",
              color: "#3730a3",
              borderRadius: "16px",
              padding: "2px 10px",
              fontSize: "12px",
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            {names.length} Customer{names.length > 1 ? "s" : ""}
          </span>
        </Tooltip>
      );
    },
  },
},
    {
      name: "start_date",
      label: "From Date",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const val = formData[dataIndex]?.start_date;
          return <p>{val ? dayjs(val).format("DD MMM YYYY, hh:mm A") : "-"}</p>;
        },
      },
    },
    {
      name: "end_date",
      label: "To Date",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const val = formData[dataIndex]?.end_date;
          return <p>{val ? dayjs(val).format("DD MMM YYYY, hh:mm A") : "-"}</p>;
        },
      },
    },
    {
      name: "active_status",
      label: "Status",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const promo = formData?.[dataIndex];
          if (!promo) return null;

          return (
            <Switch
              size="small"
              color={promo.active_status === 1 ? "success" : "error"}
              checked={promo.active_status === 1}
              onChange={() => {
                dispatch(
                  showConfirmModal({
                    msg: promo.active_status === 1 ? "Deactivate" : "Activate",
                    action: () => toggleStatus(promo),
                  }),
                );
              }}
              style={{ marginBottom: "5px" }}
            />
          );
        },
      },
    },

    {
      name: "Edit",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const item = formData[dataIndex];
          const status = item?.active_status;

          return (
            <Button
              disabled={status === 0}
              onClick={() => {
                setSelectedRow(item);

                reset({
                  promo_code: item.promo_code,
                  min_amount: item.min_amount,
                  max_amount: item.max_amount,
                  type: item.type,
                  discount: item.discount,
                  start_date: dayjs(item.start_date),
                  end_date: dayjs(item.end_date),
                  terms_conditions: item.terms_conditions,
                  promo_applicable_count: item.promo_applicable_count,
                  customer_ids:
                    item.customer_ids?.map((custId) => {
                      const cust = customerList?.find(
                        (c) => c.customer_id === custId,
                      );
                      return {
                        value: custId,
                        label: cust
                          ? `${cust.customer_name} - ${cust.customer_mobile}`
                          : custId,
                        key: custId,
                      };
                    }) || [],
                });

                setOpen(true);
              }}
            >
              <EditIcon
                sx={{ color: status === 0 ? grey[600] : "secondary" }}
              />
            </Button>
          );
        },
      },
    },
    // {
    //   name: "Delete",
    //   options: {
    //     customBodyRenderLite: (dataIndex) => {
    //       const status = formData[dataIndex]?.active_status;

    //       return (
    //         <div className="actionBtns">
    //           <Button
    //             disabled={status === 1 ? true : false}
    //             aria-label="edit"
    //             onClick={() => {
    //               dispatch(
    //                 showConfirmModal({
    //                   msg: "Delete",
    //                   action: () => deleteClickHandler(formData[dataIndex]?.id),
    //                 })
    //               );
    //             }}
    //           >
    //             <DeleteOutlineOutlinedIcon
    //               color="error"
    //               fontSize="medium"
    //               sx={{ color: status === 1 ? grey[600] : "red" }}
    //             />
    //           </Button>
    //         </div>
    //       );
    //     },
    //   },
    // },
  ];

  const handleClose = () => {
    setOpen(false);
    setSelectedRow(null);
    reset();
  };

  const onSubmit = (data) => {
    saveForm(data);
  };

  // Runs when yup validation fails - without this, a bad/mismatched
  // schema blocks the Save button with zero visible feedback.
  const onInvalid = (formErrors) => {
    console.error("Customer Promo validation errors:", formErrors);
    const firstError = Object.values(formErrors)[0];
    toast.error(firstError?.message || "Please check the highlighted fields");
  };

  const toggleStatus = async (promo) => {
    try {
      setLoader(true);

      if (!promo?.id && !promo?.cust_promo_id) {
        toast.error("Promo ID not found.");
        return;
      }

      const updatedPromo = {
        ...promo,
        active_status: promo.active_status === 1 ? 0 : 1,
      };

      const response = await axios.put(
        `/custom_promos/${promo.id || promo.cust_promo_id}`,
        updatedPromo,
      );

      toast.success(response?.data?.message || "Status updated");
      loadInitialData();
    } catch (error) {
      console.error("Toggle Status Error:", error.response || error);
      toast.error(error?.response?.data?.message || error.message);
    } finally {
      setLoader(false);
    }
  };

  const saveForm = async (data) => {
    setLoader(true);
    try {
      let response;

      // 👇 Format the dates
      if (data.start_date) {
        data.start_date = dayjs(data.start_date).format("YYYY-MM-DD HH:mm:ss");
      }

      if (data.end_date) {
        data.end_date = dayjs(data.end_date).format("YYYY-MM-DD HH:mm:ss");
      }

      data["promo_code"] = data?.promo_code?.toUpperCase();

      // Convert the selected customer options back to an array of ids
      data["customer_ids"] =
        data?.customer_ids?.map((item) => item.value) || [];

      // Ensure applicable count is sent as a number
      data["promo_applicable_count"] = data?.promo_applicable_count
        ? Number(data.promo_applicable_count)
        : null;

      const rowId = selectedRow?.id || selectedRow?.cust_promo_id;
      console.log("Saving custom promo payload:", data);

      if (rowId) {
        response = await axios.put(`/custom_promos/${rowId}`, data);
      } else {
        response = await axios.post("/custom_promos", data);
      }

      if (!response?.data?.error) {
        toast.success(response?.data?.message);
        loadInitialData();
        setOpen(false);
        setSelectedRow(null);
        reset();
      } else {
        let errDesc = "";
        if (response?.data?.data) {
          Object.values(response?.data?.data).forEach((error, index) => {
            errDesc =
              index === 0 ? errDesc + error + "\n " : errDesc + error + "\n ";
          });
        } else {
          errDesc = response?.data?.message;
        }

        dispatch(
          showModal({
            msg: errDesc,
            error: response?.data?.error,
          }),
        );
        setOpen(false);
      }
    } catch (error) {
      console.error("Save Custom Promo Error:", error?.response || error);
      dispatch(
        showModal({
          msg: error?.response?.data?.message || error?.message,
          error: true,
        }),
      );
      setOpen(false);
    } finally {
      setLoader(false);
    }
  };

  // Plain JSX (not a nested function component) so it updates in
  // place instead of unmounting/remounting on every state change.
  const createEditDialogContent = (
    <Dialog open={open} onClose={handleClose} maxWidth={"md"} fullWidth>
      <DialogTitle className="text-primary">
        {selectedRow ? "Edit Customer Promo" : "New Customer Promo"}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} columnSpacing={{ xs: 5 }}>
          <Grid item xs={6}>
            <FormInputText
              name="promo_code"
              control={control}
              label="Promo Code"
              errors={errors}
              mandatory="true"
              defaultValue={selectedRow?.promo_code}
            />
          </Grid>
          <Grid item xs={6}>
            <FormInputText
              name="min_amount"
              control={control}
              label="Minimum Amount"
              errors={errors}
              type="number"
              defaultValue={selectedRow?.min_amount}
            />
          </Grid>
          <Grid item xs={6}>
            <FormInputText
              name="max_amount"
              control={control}
              label="Maximum Amount"
              errors={errors}
              type="number"
              defaultValue={selectedRow?.max_amount}
            />
          </Grid>
          <Grid item xs={6}>
            <FormInputDropdown
              name="type"
              control={control}
              label="Type"
              type="single"
              options={[
                {
                  id: "AMOUNT",
                  name: "Amount (₹)",
                },
                {
                  id: "PERCENTAGE",
                  name: "Percentage (%)",
                },
              ]}
              errors={errors}
              defaultValue={selectedRow?.type}
              mandatory="true"
            />
          </Grid>
          <Grid item xs={6}>
            <FormInputText
              name="discount"
              control={control}
              label="Amount / Percent"
              errors={errors}
              mandatory="true"
              type="number"
              defaultValue={selectedRow?.discount}
            />
          </Grid>
          <Grid item xs={6}>
            <FormInputText
              name="promo_applicable_count"
              control={control}
              label="Applicable Count"
              errors={errors}
              type="number"
              defaultValue={selectedRow?.promo_applicable_count}
            />
          </Grid>
          <Grid item xs={12}>
            {/* ✅ Reusable across modules: same CustomerPickerField +
                useCustomerSearch hook can be dropped into any other
                screen that needs a fast customer picker. */}
            <CustomerPickerField
              name="customer_ids"
              control={control}
              errors={errors}
              multiple
              customerOptions={customerOptions}
              searchCustomers={searchCustomers}
              defaultValue={
                selectedRow?.customer_ids?.map((custId) => {
                  const cust = customerList?.find(
                    (c) => c.customer_id === custId,
                  );
                  return {
                    value: custId,
                    label: cust
                      ? `${cust.customer_name} - ${cust.customer_mobile}`
                      : custId,
                    key: custId,
                  };
                }) || []
              }
            />
          </Grid>
          <Grid item xs={6}>
            <FormInputDate
              name="start_date"
              control={control}
              label="From Date"
              type="date"
              errors={errors}
              defaultValue={
                selectedRow?.start_date ? dayjs(selectedRow.start_date) : null
              }
            />
          </Grid>
          <Grid item xs={6}>
            <FormInputDate
              name="end_date"
              control={control}
              label="To Date"
              type="date"
              errors={errors}
              defaultValue={
                selectedRow?.end_date ? dayjs(selectedRow.end_date) : null
              }
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="terms_conditions"
              control={control}
              defaultValue={selectedRow?.terms_conditions || ""}
              render={({ field: { onChange, value } }) => (
                <textarea
                  className="custom-textarea"
                  aria-label="terms and conditions"
                  placeholder="Terms & Conditions"
                  value={value}
                  style={{ width: "100%", height: "100px" }}
                  onChange={onChange}
                  rowsMin={3}
                />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="contained" color="error">
          Cancel
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSubmit(onSubmit, onInvalid)}
          startIcon={<SaveIcon />}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );

  const options = {
    selectableRows: false,
    filter: false,
    download: true,
    viewColumns: true,
    print: false,
  };
  return (
    <>
      {loader ? (
        <Loader />
      ) : (
        <>
          <h4 className="text-dark">Custom Promo</h4>
          <MUIDataTable
            data={formData?.length > 0 ? formData : []}
            columns={columns}
            options={options}
            title={
              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setSelectedRow(null);
                  reset({
                    promo_code: "",
                    min_amount: "",
                    max_amount: "",
                    type: "",
                    discount: "",
                    promo_applicable_count: "",
                    customer_ids: [],
                    start_date: null,
                    end_date: null,
                    terms_conditions: "",
                  });
                  setOpen(true);
                }}
              >
                New Promo
              </Button>
            }
          />
        </>
      )}
      {createEditDialogContent}
    </>
  );
}

export default CustomerPromo;
